import * as React from "react";

/**
 * Where column `colId` of the AG Grid that contains `element` starts, in
 * pixels from `element`'s left edge, or `null` while the grid shows no such
 * column (it is hidden) or `enabled` is false. Kept current as the user
 * resizes, moves, shows or hides columns and as the page reflows: AG Grid
 * rewrites its header cells' inline position and size on every such change,
 * and adds or removes a cell when a column is shown or hidden, so the header
 * is watched for both, and `element` for changes to its own width.
 */
export function useGridColumnLeft(
  element: HTMLElement | null,
  colId: string,
  enabled: boolean,
): number | null {
  const [left, setLeft] = React.useState<number | null>(null);

  React.useEffect(() => {
    const header = element
      ?.closest(".ag-root-wrapper")
      ?.querySelector<HTMLElement>(".ag-header");
    if (
      !enabled ||
      element === null ||
      header === null ||
      header === undefined
    ) {
      setLeft(null);
      return;
    }
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const cell = header.querySelector<HTMLElement>(
          `.ag-header-cell[col-id="${colId}"]`,
        );
        setLeft(
          cell === null
            ? null
            : Math.round(
                cell.getBoundingClientRect().left -
                  element.getBoundingClientRect().left,
              ),
        );
      });
    };
    measure();
    const mutations = new MutationObserver(measure);
    mutations.observe(header, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    const resizes = new ResizeObserver(measure);
    resizes.observe(element);
    return () => {
      cancelAnimationFrame(frame);
      mutations.disconnect();
      resizes.disconnect();
    };
  }, [element, colId, enabled]);

  return left;
}
